<template>
  <div class="note-container">
    <div class="note-header">
      <h2>我的笔记</h2>
      <span v-if="username" class="username">{{ username }}的笔记本</span>
    </div>
    <div class="note-content">
      <textarea
        v-model="noteContent"
        placeholder="在这里输入你的笔记..."
        @input="autoSave"
      ></textarea>
    </div>
    <div class="note-actions">
      <button @click="saveNote" class="save-button" :disabled="!username">
        保存笔记
      </button>
      <!-- 统一使用 saveStatus 变量 -->
      <span v-if="saveStatus" class="save-status">{{ saveStatus }}</span>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { mapState } from "vuex";

const API_BASE_URL = process.env.VUE_APP_API_BASE;
export default {
  name: "UserNote",

  data() {
    return {
      noteContent: "",
      saveStatus: "", // 统一使用小写开头的 saveStatus
      autoSaveTimer: null,
    };
  },

  computed: {
    ...mapState(["username"]),
  },

  created() {
    if (this.username) {
      this.loadNote();
    }
  },

  watch: {
    username: {
      handler(newUsername) {
        if (newUsername) {
          this.loadNote();
        } else {
          this.noteContent = "";
        }
      },
      immediate: true,
    },
  },

  methods: {
    async loadNote() {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/userNote.json`);
        const notes = response.data;

        if (notes && notes[this.username]) {
          this.noteContent = notes[this.username];
          console.log(`已加载${this.username}的笔记`);
        } else {
          this.noteContent = "";
          console.log("该用户暂无笔记");
        }
      } catch (error) {
        console.error("加载笔记失败:", error);
        this.saveStatus = "加载笔记失败";
      }
    },

    async saveNote() {
      if (!this.username) {
        this.saveStatus = "请先登录";
        return;
      }

      try {
        await axios.post(`${API_BASE_URL}/api/saveNote`, {
          username: this.username,
          content: this.noteContent,
        });

        this.saveStatus = "保存成功！"; // 更新 saveStatus
        console.log(`${this.username}的笔记已保存`);

        // 2秒后清空保存状态
        setTimeout(() => {
          this.saveStatus = "";
        }, 2000);
      } catch (error) {
        console.error("保存笔记失败:", error);
        this.saveStatus = "保存失败，请重试";
      }
    },

    autoSave() {
      if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = setTimeout(() => {
        if (this.username) {
          this.saveNote();
        }
      }, 2000);
    },
  },

  beforeUnmount() {
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
      if (this.username) {
        this.saveNote();
      }
    }
  },
};
</script>

<style scoped>
/* 全局样式 */
html,
body {
  height: 100%;
  margin: 0;
  padding: 0;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #f5f7fa, #c3cfe2);
}
/* 笔记容器 */
.note-container {
  width: 90%;
  max-width: 600px;
  padding: 24px;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  background-color: #ffffff;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
}
.note-container:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.2);
}
/* 笔记头部 */
.note-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 20px;
}
.note-header h2 {
  margin: 0;
  font-size: 24px;
  font-weight: bold;
  color: #333;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.1);
  line-height: 1.5;
}
.username {
  margin-top: 4px;
  color: #4caf50;
  font-size: 14px;
  font-weight: 500;
}
/* 笔记内容 */
.note-content {
  margin-bottom: 20px;
}
.note-content textarea {
  width: calc(100% - 24px); /* 调整宽度，确保左右对称 */
  height: 200px;
  line-height: 1.6;
  font-size: 16px;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  resize: none;
  outline: none;
  background-color: #f9f9f9;
  overflow-y: auto;
  transition: all 0.3s ease;
}
.note-content textarea:focus {
  border-color: #4caf50;
  box-shadow: 0 0 8px rgba(76, 175, 80, 0.3);
  background-color: #ffffff;
}
/* 笔记操作 */
.note-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}
.save-button {
  padding: 12px 24px;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(76, 175, 80, 0.2);
}
.save-button:hover {
  background-color: #45a049;
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(76, 175, 80, 0.3);
}
.save-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}
.save-status {
  color: #666;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  padding-top: 25px;
}
/* 响应式设计 */
@media (max-width: 600px) {
  .note-container {
    width: 95%;
    max-width: 100%;
    padding: 16px;
  }
  .note-header h2 {
    font-size: 20px;
  }
  .username {
    font-size: 12px;
  }
  .note-content textarea {
    height: 150px;
    font-size: 14px;
    width: calc(100% - 24px); /* 调整宽度 */
  }
  .save-button {
    padding: 10px 20px;
    font-size: 14px;
  }
  .save-status {
    font-size: 12px;
  }
}
@media (max-width: 400px) {
  .note-container {
    padding: 12px;
  }
  .note-header h2 {
    font-size: 18px;
  }
  .note-content textarea {
    height: 120px;
    font-size: 12px;
    width: calc(100% - 24px); /* 调整宽度 */
  }
  .save-button {
    padding: 8px 16px;
    font-size: 12px;
  }
}
</style>
